class NotificationService {
    constructor() {
        this.permission = null;
        this.isSupported = 'Notification' in window;
    }

    async requestPermission() {
        if (!this.isSupported) {
            console.warn('This browser does not support notifications');
            return false;
        }

        if (this.permission === 'granted') {
            return true;
        }

        if (this.permission === 'denied') {
            console.warn('Notification permission denied');
            return false;
        }

        try {
            this.permission = await Notification.requestPermission();
            return this.permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    async showNotification(title, options = {}) {
        if (!this.isSupported || this.permission !== 'granted') {
            // Fallback to console log or other notification method
            console.log(`Notification: ${title}`, options);
            return null;
        }

        try {
            const notification = new Notification(title, {
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                ...options
            });

            // Auto close after 5 seconds
            setTimeout(() => {
                notification.close();
            }, 5000);

            return notification;
        } catch (error) {
            console.error('Error showing notification:', error);
            return null;
        }
    }

    async showChatNotification(senderName, message, leadId) {
        const title = `New message from ${senderName}`;
        const options = {
            body: message.length > 100 ? message.substring(0, 100) + '...' : message,
            tag: `chat-${leadId}`, // Prevent duplicate notifications
            data: { leadId, type: 'chat' },
            requireInteraction: false,
            silent: false
        };

        return await this.showNotification(title, options);
    }

    async showLeadNotification(leadData) {
        const title = 'New booking request';
        const options = {
            body: `${leadData.event_type} for ${leadData.guest_count} guests on ${leadData.event_date}`,
            tag: `lead-${leadData.id}`,
            data: { leadId: leadData.id, type: 'lead' },
            requireInteraction: true,
            silent: false
        };

        return await this.showNotification(title, options);
    }

    async showBookingUpdateNotification(bookingData) {
        const title = 'Booking status updated';
        const options = {
            body: `Your booking status has been updated to ${bookingData.status}`,
            tag: `booking-${bookingData.id}`,
            data: { bookingId: bookingData.id, type: 'booking' },
            requireInteraction: false,
            silent: false
        };

        return await this.showNotification(title, options);
    }

    // Handle notification click events
    setupNotificationClickHandler() {
        if (!this.isSupported) return;

        self.addEventListener('notificationclick', (event) => {
            event.notification.close();

            const data = event.notification.data;
            
            if (data) {
                switch (data.type) {
                    case 'chat':
                        // Focus on chat window or navigate to chat
                        window.focus();
                        // You can add logic to open the chat popover here
                        break;
                    case 'lead':
                        // Navigate to leads page
                        window.location.href = '/vendor/leads';
                        break;
                    case 'booking':
                        // Navigate to bookings page
                        window.location.href = '/bookings';
                        break;
                    default:
                        // Default action
                        window.focus();
                }
            } else {
                // Default action
                window.focus();
            }
        });
    }

    // Check if notifications are supported and permitted
    canShowNotifications() {
        return this.isSupported && this.permission === 'granted';
    }

    // Get current permission status
    getPermissionStatus() {
        return this.permission;
    }
}

// Create singleton instance
const notificationService = new NotificationService();

// Setup click handler
notificationService.setupNotificationClickHandler();

export default notificationService;
